import {getUserFromToken} from "./storage";
import TokenPayload from "../types/TokenPayload";
import UserRole from "../types/UserRole";
import Page from "../types/Page";
import {hasCommonValue} from "../util/common";


// Note: This function returns page keys that should be translated using the text system
// The 'name' field is now a key that corresponds to the navigation texts in texts.json
export const getPages = (): Page[] => {
    const userData: TokenPayload | null = getUserFromToken();
    if (!userData) return [{ name: "login", path: "/login" }];

    const roles: UserRole[] = userData?.roles?.map(el => el.id);
    return [
        { name: 'home', path: "/" },
        hasCommonValue(roles, roleMap.VIEW_PRESCRIPTIONS)
        && { name: 'prescriptions', path: "/prescriptions" },
        hasCommonValue(roles, roleMap.CREATE_PRESCRIPTION)
        && { name: "createPrescription", path: "/create-prescription" },
        hasCommonValue(roles, roleMap.VIEW_PRODUCTS)
        && { name: "products", path: "/products" },
        hasCommonValue(roles, roleMap.VIEW_PLACES)
        && { name: "places", path: "/places" },
        hasCommonValue(roles, roleMap.VIEW_CITIES)
        && { name: "cities", path: "/cities" },
        hasCommonValue(roles, roleMap.CREATE_USER)
        && { name: "createUser", path: "/sign-up" },
        hasCommonValue(roles, roleMap.VIEW_USERS)
        && { name: "users", path: "/users" },
        hasCommonValue(roles, roleMap.VIEW_MEDIATORS)
        && { name: "mediators", path: "/mediators" },
        hasCommonValue(roles, roleMap.VIEW_MEDIATORS)
        && { name: "mediatorScores", path: "/mediators/scores" },
        hasCommonValue(roles, roleMap.PRODUCT_USAGE_CHANGE_VIEW)
        && { name: "productUsageChange", path: "/product-usage-change" }
    ].filter(Boolean) as Array<{ name: string; path: string }>
}

export const roleMap: { [key: string]: UserRole[] } = {
    VIEW_STATISTICS: [UserRole.CONTROLLER, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    VIEW_PRESCRIPTION_STATISTICS: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    VIEW_PRESCRIPTIONS: [UserRole.CONTROLLER, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    CREATE_PRESCRIPTION: [UserRole.REPRESENTATIVE, UserRole.SUPER_ADMIN],
    EDIT_PRESCRIPTION: [UserRole.MANAGER, UserRole.CONTROLLER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    VIEW_PRODUCTS: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    CREATE_PRODUCT: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    EDIT_PRODUCT: [UserRole.SUPER_ADMIN],
    DELETE_PRODUCT: [UserRole.SUPER_ADMIN],
    VIEW_PLACES: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    CREATE_PLACE: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    EDIT_PLACE: [UserRole.SUPER_ADMIN],
    VIEW_CITIES: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    CREATE_CITY: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    EDIT_CITY: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    DELETE_CITY: [UserRole.SUPER_ADMIN],
    VIEW_USERS: [UserRole.SUPER_ADMIN],
    CREATE_USER: [UserRole.SUPER_ADMIN],
    EDIT_USER: [UserRole.SUPER_ADMIN],
    VIEW_MEDIATORS: [UserRole.CONTROLLER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    CREATE_MEDIATOR: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    EDIT_MEDIATOR: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    PRODUCT_USAGE_CHANGE_VIEW: [UserRole.CONTROLLER, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER],
    FLAG_PRESCRIPTION: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    VIEW_FLAGGED_PRESCRIPTIONS: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
    VIEW_PRESCRIPTION_LOGS: [UserRole.SUPER_ADMIN]
}


export const operationNames: { [key: string]: string } = {
    VIEW_STATISTICS: "VIEW_STATISTICS",
    VIEW_PRESCRIPTION_STATISTICS: "VIEW_PRESCRIPTION_STATISTICS",
    VIEW_PRESCRIPTIONS: "VIEW_PRESCRIPTIONS",
    CREATE_PRESCRIPTION: "CREATE_PRESCRIPTION",
    EDIT_PRESCRIPTION: "EDIT_PRESCRIPTION",
    VIEW_PRODUCTS: "VIEW_PRODUCTS",
    CREATE_PRODUCT: "CREATE_PRODUCT",
    EDIT_PRODUCT: "EDIT_PRODUCT",
    DELETE_PRODUCT: "DELETE_PRODUCT",
    VIEW_PLACES: "VIEW_PLACES",
    CREATE_PLACE: "CREATE_PLACE",
    EDIT_PLACE: "EDIT_PLACE",
    VIEW_CITIES: "VIEW_CITIES",
    CREATE_CITY: "CREATE_CITY",
    EDIT_CITY: "EDIT_CITY",
    DELETE_CITY: "DELETE_CITY",
    VIEW_USERS: "VIEW_USERS",
    CREATE_USER: "CREATE_USER",
    EDIT_USER: "EDIT_USER",
    VIEW_MEDIATORS: "VIEW_MEDIATORS",
    CREATE_MEDIATOR: "CREATE_MEDIATOR",
    EDIT_MEDIATOR: "EDIT_MEDIATOR",
    PRODUCT_USAGE_CHANGE_VIEW: "PRODUCT_USAGE_CHANGE_VIEW",
    FLAG_PRESCRIPTION: "FLAG_PRESCRIPTION",
    VIEW_FLAGGED_PRESCRIPTIONS: "VIEW_FLAGGED_PRESCRIPTIONS",
    VIEW_PRESCRIPTION_LOGS: "VIEW_PRESCRIPTION_LOGS"
}

export const userCan = (requiredRoles: UserRole[]): boolean => {
    const userData: TokenPayload | null = getUserFromToken();
    if (!userData) return false;
    const userRoles: UserRole[] = userData?.roles?.map(el => el.id);
    return hasCommonValue(userRoles, requiredRoles)
}

export const userHasAccess = (operationName: string): boolean => {
    const requiredRoles: UserRole[] = roleMap[operationName];
    return userCan(requiredRoles);
}